import { useState } from 'react';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { useToast } from '../../context/ToastContext';
import './Developers.css';

const MOCK_API_KEYS = [
  { id: 1, name: 'Production API Key', key: 'sk_live_...7x2f', created: 'Jan 1, 2026', lastUsed: '2 min ago', status: 'active' },
  { id: 2, name: 'Development Key', key: 'sk_test_...3k9m', created: 'Dec 15, 2025', lastUsed: '1 hour ago', status: 'active' },
  { id: 3, name: 'CI/CD Pipeline', key: 'sk_live_...8n4p', created: 'Nov 20, 2025', lastUsed: '5 days ago', status: 'active' },
];

const MOCK_WEBHOOKS = [
  { id: 1, url: 'https://api.example.com/webhooks/auth1', events: ['user.created', 'user.login'], status: 'active' },
  { id: 2, url: 'https://hooks.slack.com/services/...', events: ['fraud.detected'], status: 'active' },
];

const Developers = () => {
  const [activeTab, setActiveTab] = useState('api-keys');
  const { showToast } = useToast();

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard', 'success');
  };

  return (
    <div className="developers-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Developer Tools</h1>
          <p className="page-subtitle">Manage API keys, webhooks, and integrations</p>
        </div>
        <div className="page-actions">
          <Button variant="secondary" icon="eye">View Docs</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'api-keys' ? 'active' : ''}`}
            onClick={() => setActiveTab('api-keys')}
          >
            <Icon name="key" size="sm" />
            API Keys
          </button>
          <button
            className={`tab ${activeTab === 'webhooks' ? 'active' : ''}`}
            onClick={() => setActiveTab('webhooks')}
          >
            <Icon name="zap" size="sm" />
            Webhooks
          </button>
          <button
            className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            <Icon name="activity" size="sm" />
            API Logs
          </button>
        </div>
      </div>

      {/* API Keys Tab */}
      {activeTab === 'api-keys' && (
        <section className="section">
          <div className="section-header">
            <h3 className="section-title">API Keys</h3>
            <Button variant="primary" size="sm" icon="plus">Create API Key</Button>
          </div>

          <div className="api-keys-list">
            {MOCK_API_KEYS.map((apiKey) => (
              <div key={apiKey.id} className="api-key-card">
                <div className="api-key-info">
                  <div className="api-key-name">{apiKey.name}</div>
                  <div className="api-key-meta">
                    Created {apiKey.created} • Last used {apiKey.lastUsed}
                  </div>
                </div>
                <div className="api-key-value">
                  <code>{apiKey.key}</code>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(apiKey.key)}
                    aria-label="Copy API key"
                  >
                    <Icon name="clipboard" size="sm" />
                  </button>
                </div>
                <div className="api-key-actions">
                  <span className={`status-dot ${apiKey.status}`}></span>
                  <Button variant="ghost" size="xs" icon="edit" iconOnly aria-label="Edit" />
                  <Button variant="ghost" size="xs" icon="trash" iconOnly aria-label="Delete" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Webhooks Tab */}
      {activeTab === 'webhooks' && (
        <section className="section">
          <div className="section-header">
            <h3 className="section-title">Webhooks</h3>
            <Button variant="primary" size="sm" icon="plus">Add Webhook</Button>
          </div>

          <div className="webhooks-list">
            {MOCK_WEBHOOKS.map((webhook) => (
              <div key={webhook.id} className="webhook-card">
                <div className="webhook-info">
                  <div className="webhook-url">
                    <code>{webhook.url}</code>
                  </div>
                  <div className="webhook-events">
                    {webhook.events.map((event, idx) => (
                      <span key={idx} className="event-badge">{event}</span>
                    ))}
                  </div>
                </div>
                <div className="webhook-actions">
                  <span className={`status-dot ${webhook.status}`}></span>
                  <Button variant="ghost" size="xs" icon="refresh" iconOnly aria-label="Test" />
                  <Button variant="ghost" size="xs" icon="edit" iconOnly aria-label="Edit" />
                  <Button variant="ghost" size="xs" icon="trash" iconOnly aria-label="Delete" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* API Logs Tab */}
      {activeTab === 'logs' && (
        <section className="section">
          <div className="section-header">
            <h3 className="section-title">API Request Logs</h3>
            <Button variant="secondary" size="sm" icon="refresh">Refresh</Button>
          </div>

          <div className="api-logs">
            <div className="log-entry success">
              <span className="log-method">POST</span>
              <span className="log-path">/api/v1/auth/login</span>
              <span className="log-status">200</span>
              <span className="log-time">23ms</span>
              <span className="log-timestamp">12:45:23</span>
            </div>
            <div className="log-entry success">
              <span className="log-method">GET</span>
              <span className="log-path">/api/v1/users/me</span>
              <span className="log-status">200</span>
              <span className="log-time">15ms</span>
              <span className="log-timestamp">12:45:22</span>
            </div>
            <div className="log-entry error">
              <span className="log-method">POST</span>
              <span className="log-path">/api/v1/auth/register</span>
              <span className="log-status">400</span>
              <span className="log-time">8ms</span>
              <span className="log-timestamp">12:45:20</span>
            </div>
            <div className="log-entry success">
              <span className="log-method">GET</span>
              <span className="log-path">/api/v1/tenants</span>
              <span className="log-status">200</span>
              <span className="log-time">45ms</span>
              <span className="log-timestamp">12:45:18</span>
            </div>
          </div>
        </section>
      )}

      {/* Quick Start Guide */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">Quick Start</h3>
        </div>

        <div className="code-block">
          <div className="code-header">
            <span>Install SDK</span>
            <button className="copy-btn" onClick={() => copyToClipboard('npm install @auth1/sdk')}>
              <Icon name="clipboard" size="sm" />
            </button>
          </div>
          <pre><code>npm install @auth1/sdk</code></pre>
        </div>
      </section>
    </div>
  );
};

export default Developers;
